/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, JSX } from 'react';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';

import { AccountTransferRequest } from './types';
import { useOpenModule } from '../../../providers/OpenModule.provider';
import { useNavMenu } from '../../../providers/NavMenu.provider';
import { AccountTransferDialogueHandler, DialogueHandlerProps } from '../Dialogue/AccountTransfer/types';
import AccountTransferDialogueContext, { AccountTransferCtx } from '../Dialogue/AccountTransfer/AccountTransferContext';
import AccountTransferDialogue from '../Dialogue/AccountTransfer/AccountTransferDialogue';
import usePostAccountTransferFrom from '../../../hooks/useApi/usePostAccountTransferFrom';
import usePostAccountTransferTo from '../../../hooks/useApi/usePostAccountTransferTo';
import { useClientData } from '../../../providers/ClientData.provider';

const { Box, SubTitle, Table, Button, TextField, formattedCurrency } = vp;

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
};

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
};

type AccountProps = {
  values: AccountTransferRequest;
  subAccounts: Record<string, vp.SubAccountsLists>;
  onBack: () => void;
};

type FC = FunctionComponent<AccountProps>;

const AccountTransferReview: FC = ({ values, subAccounts, onBack }) => {
  const history = useHistory();
  const { response } = useClientData();
  const fromMain = response.AccountID === values.From;
  const toMain = response.AccountID === values.To;
  const { setSubNavActiveModule } = useNavMenu();
  const { setOpenModule } = useOpenModule();
  const [accountTransferFromResponse, postAccountTransferFrom] = usePostAccountTransferFrom({});
  const [accountTransferToResponse, postAccountTransferTo] = usePostAccountTransferTo({});

  const [shouldTransferBankAccount, setShouldTransferBankAccount] = useState(false);
  const [shouldTransferFrom, setShouldTransferFrom] = useState(false);
  const [showAccountTransferErrorAlert, setShowAccountTransferErrorAlert] = useState(false);
  const [accountTransferError, setAccountTransferError] = useState('');

  const AccountTransfer: AccountTransferCtx = {
    showAccountTransferErrorAlert,
    accountTransferError,
  };

  const defaultValues = {
    Currency: '',
    From: '',
    To: '',
    Amount: 0.0,
  };

  const handlers = {
    handleAccountTransferDialogue: (handlerProps: DialogueHandlerProps<AccountTransferDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;
      const dialogueHandlers: AccountTransferDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowAccountTransferErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },

    onAccountTransfer: (_values: AccountTransferRequest) => {
      // need to fix
      if (fromMain) {
        setShouldTransferBankAccount(true);
        postAccountTransferTo({
          Amount: Number(values.Amount || 0),
          DebitorClientAccountID: '',
          RecipientAccountID: values.To || '',
          RecipientClientAccountID: '',
          Currency: values.Currency || '',
          ClientReferenceNumber: '',
          Notes: '',
          IdempotencyKey: '',
        });
      } else if (toMain) {
        setShouldTransferFrom(true);
        postAccountTransferFrom({
          Amount: Number(values.Amount || 0),
          DebitorAccountID: values.From || '',
          DebitorClientAccountID: '',
          RecipientClientAccountID: '',
          Currency: values.Currency || '',
          ClientReferenceNumber: '',
          Notes: '',
          IdempotencyKey: '',
        });
      } else {
        setShouldTransferFrom(true);
        postAccountTransferFrom({
          Amount: Number(values.Amount || 0),
          DebitorAccountID: values.From || '',
          DebitorClientAccountID: '',
          RecipientClientAccountID: '',
          Currency: values.Currency || '',
          ClientReferenceNumber: '',
          Notes: '',
          IdempotencyKey: '',
        });
      }
    },

    handleBack: () => {
      onBack();
    },
  };

  useEffect(() => {
    if (shouldTransferFrom && accountTransferFromResponse.Success) {
      if (!toMain) {
        setShouldTransferBankAccount(true);
        postAccountTransferTo({
          Amount: Number(values.Amount || 0),
          RecipientClientAccountID: '',
          DebitorClientAccountID: '',
          RecipientAccountID: values.To || '',
          Currency: values.Currency || '',
          ClientReferenceNumber: '',
          Notes: '',
          ParentTransactionID: '',
          IdempotencyKey: '',
        });
      }
    }
  }, [accountTransferFromResponse.Success, shouldTransferFrom]);

  useEffect(() => {
    if (shouldTransferFrom && toMain) {
      const { Success, ErrorMessage } = accountTransferFromResponse;
      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/AccountTransfer/${accountTransferFromResponse.TransactionID}/${values.Currency}/`,
          state: {
            transactionId: accountTransferFromResponse.TransactionID || '',
            activeModule: 'accountTransfer',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowAccountTransferErrorAlert(true);
        setAccountTransferError(ErrorMessage);
      }
      setShouldTransferFrom(false);
    }

    if (shouldTransferBankAccount) {
      const { Success, ErrorMessage } = accountTransferToResponse;

      if (Success === true) {
        history.push({
          pathname: `/transactions-history/details/AccountTransfer/${accountTransferToResponse.TransactionID}/${values.Currency}/`,
          state: {
            transactionId: accountTransferToResponse.TransactionID || '',
            activeModule: 'accountTransfer',
            hasNewTransactionButton: true,
          },
        });
        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
      } else if (Success === false) {
        setShowAccountTransferErrorAlert(true);
        setAccountTransferError(ErrorMessage);
      }
      setShouldTransferBankAccount(false);
    }
  }, [accountTransferToResponse, accountTransferFromResponse]);

  return (
    <Formik initialValues={defaultValues} onSubmit={handlers.onAccountTransfer} validateOnChange validateOnBlur>
      {({ handleSubmit }) => (
        <AccountTransferDialogueContext.Provider value={AccountTransfer}>
          <form onSubmit={handleSubmit}>
            <Box flx="col">
              <Box flx="col" width="50%" mt="md">
                <SubTitle subTitle="Transaction Details" />
                <Box mb="md">
                  <Table {...getTableProps('transactionDetails')} />
                </Box>
              </Box>
              <Box width="100%" jc="fe" mt="lg">
                <Button id="transfer-account-btn" label="Transfer" type="submit" />
              </Box>
            </Box>
          </form>
          <AccountTransferDialogue handleDialogue={handlers.handleAccountTransferDialogue} />
        </AccountTransferDialogueContext.Provider>
      )}
    </Formik>
  );

  function buildSubAccountOptions(lists: vp.SubAccountsLists[]) {
    return lists.sort((a, b) => {
      const nameA = a.AccountName.toUpperCase(); // Convert name to uppercase for case-insensitive sorting
      const nameB = b.AccountName.toUpperCase();

      if (nameA < nameB) {
        return -1; // a should be before b
      }
      if (nameA > nameB) {
        return 1; // a should be after b
      }

      return 0; // a and b are equal
    });
  }

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const BankAccountsOptions = buildSubAccountOptions(Object.values(subAccounts));
    const handlersTable: TablePropsHandlers = {
      transactionDetails: () => {
        const transactionDetailsPair: KeyValuePair[] = [
          {
            display: 'From',
            value: (
              <TextField
                name="account-from-confirmation"
                variant="background"
                placeholder={
                  fromMain
                    ? response.AccountName
                    : BankAccountsOptions.find((subaccount) => subaccount.AccountID === values.From)?.AccountName || ''
                }
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: 'To',
            value: (
              <TextField
                name="account-to-confirmation"
                variant="background"
                placeholder={
                  toMain
                    ? response.AccountName
                    : BankAccountsOptions.find((subaccount) => subaccount.AccountID === values.To)?.AccountName || ''
                }
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
          {
            display: 'Amount',
            value: (
              <TextField
                name="amount-confirmation"
                variant="background"
                placeholder={
                  `${getCurrencySign(values.Currency)}${formattedCurrency(String(values?.Amount))}  ${
                    values.Currency
                  }` || ''
                }
                placeholderColor
                disabled
                onBlur={() => {}}
                onChange={() => {}}
              />
            ),
          },
        ];

        const transactionDetails: vp.TableProps = {
          name: 'from-transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: 'From Transaction Details' },
            detailValue: { header: '' },
          },
          rows:
            transactionDetailsPair.map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })) || [],
        };

        return transactionDetails;
      },
    };

    return handlersTable[handler]();
  }

  function getCurrencySign(currency: string) {
    return currency === 'CAD' || currency === 'USD' || currency === 'AUD'
      ? '$'
      : currency === 'DKK' || currency === 'SEK' || currency === 'NOK'
      ? 'kr'
      : currency === 'GBP'
      ? '£'
      : currency === 'EUR'
      ? '€'
      : currency === 'PLN'
      ? 'zł'
      : '$';
  }
};

export default AccountTransferReview;
